import { useState, useEffect, useRef } from "react";
import { Loader2, ChevronDown, Info } from "lucide-react";
import { MainLayout } from "../components/MainLayout";
import { useApp } from "../components/AppContext";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useGetExtra } from "../api/extras/useGetExtra";
import { useUpdateExtra } from "../api/extras/useUpdateExtra";
import { usePrmAssistant } from "../hooks/usePrmAssistant";
import { useGetCountries } from "../api/countries/useGetCountries";
import { useGetOccupations } from "../api/occupations/useGetOccupations";
import { isValidIBAN, isValidBIC } from "ibantools";
import taxIdValidator from "german-tax-id-validator";

export function PrmData() {
	const { navigateDirectly } = useApp();
	const { user } = useCurrentUser();
	const { extra, loading: extraLoading, refetch: refetchExtra } = useGetExtra(user?.extraId);
	const { updateExtra } = useUpdateExtra();
	const {
		assistant,
		loading: assistantLoading,
		createAssistant,
		updateAssistant,
		deleteAssistant,
		refetch: refetchAssistant,
	} = usePrmAssistant(user?.extraId);
	const { countries } = useGetCountries();
	const { occupations } = useGetOccupations();

	const [disabilityType, setDisabilityType] = useState("");
	const [saving, setSaving] = useState(false);

	const [needsAssistant, setNeedsAssistant] = useState(false);
	const [assistantForm, setAssistantForm] = useState({
		first_name: "",
		last_name: "",
		date_of_birth: "",
		city_of_birth: "",
		country_of_birth: "" as string,
		geburtsname: "",
		nationality: "" as string,
		id_number: "",
		main_occupation_id: "" as string,
		street: "",
		postcode: "",
		city: "",
		iban: "",
		bic: "",
		idnr: "",
		sozialversicherung: "",
		insurance_type: "",
		insurance_company: "",
	});
	const [isDirty, setIsDirty] = useState(false);
	const [noAssistantSozialversicherung, setNoAssistantSozialversicherung] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [showAssistantIdInfoModal, setShowAssistantIdInfoModal] = useState(false);
	const savedDataRef = useRef(assistantForm);

	useEffect(() => {
		if (extra) {
			setDisabilityType(extra.prm_disability_type || "");
		}
	}, [extra]);

	useEffect(() => {
		if (assistant) {
			const loaded = {
				first_name: assistant.first_name || "",
				last_name: assistant.last_name || "",
				date_of_birth: assistant.date_of_birth
					? String(assistant.date_of_birth).split("T")[0]
					: "",
				city_of_birth: assistant.city_of_birth || "",
				country_of_birth: assistant.country_of_birth ? String(assistant.country_of_birth) : "",
				geburtsname: assistant.geburtsname || "",
				nationality: assistant.nationality ? String(assistant.nationality) : "",
				id_number: assistant.id_number || "",
				main_occupation_id: assistant.main_occupation_id ? String(assistant.main_occupation_id) : "",
				street: assistant.street || "",
				postcode: assistant.postcode || "",
				city: assistant.city || "",
				iban: assistant.iban || "",
				bic: assistant.bic || "",
				idnr: assistant.idnr || "",
				sozialversicherung: assistant.sozialversicherung || "",
				insurance_type: assistant.insurance_type || "",
				insurance_company: assistant.insurance_company || "",
			};
			setAssistantForm(loaded);
			savedDataRef.current = loaded;
			setIsDirty(false);
			setNeedsAssistant(true);
			if (!assistant.sozialversicherung) {
				setNoAssistantSozialversicherung(true);
			}
		}
	}, [assistant]);

	const handleAssistantChange = (field: string, value: string) => {
		// Auto-format SV number
		if (field === "sozialversicherung") {
			value = value.slice(0, 12);
			if (value.length >= 9) {
				const char9 = value.charAt(8);
				if (/[a-zA-Z]/.test(char9)) {
					value = value.slice(0, 8) + char9.toUpperCase() + value.slice(9);
				}
			}
		}

		const updated = { ...assistantForm, [field]: value };
		setAssistantForm(updated);
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
		const dirty = Object.keys(updated).some(
			(k) =>
				updated[k as keyof typeof updated] !==
				savedDataRef.current[k as keyof typeof savedDataRef.current],
		);
		setIsDirty(dirty);
	};

	const handleAssistantSozialCheckbox = (checked: boolean) => {
		setNoAssistantSozialversicherung(checked);
		if (checked) {
			setAssistantForm((prev) => ({ ...prev, sozialversicherung: "" }));
			setErrors((prev) => ({ ...prev, sozialversicherung: "" }));
		} else {
			setAssistantForm((prev) => ({ ...prev, city_of_birth: "", country_of_birth: "", geburtsname: "" }));
			setErrors((prev) => ({ ...prev, city_of_birth: "", country_of_birth: "", geburtsname: "" }));
		}
		setIsDirty(true);
	};

	const birthFields = ["city_of_birth", "country_of_birth", "geburtsname"];
	const allAssistantFieldsFilled = Object.entries(assistantForm).every(
		([key, v]) => {
			if (key === "sozialversicherung" && noAssistantSozialversicherung) return true;
			if (birthFields.includes(key) && !noAssistantSozialversicherung) return true;
			return String(v).trim() !== "";
		},
	);

	const validateAssistantForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		// IBAN validation
		if (!assistantForm.iban.trim()) {
			newErrors.iban = "IBAN ist erforderlich";
		} else {
			const ibanCleaned = assistantForm.iban.replace(/\s/g, "");
			if (!isValidIBAN(ibanCleaned)) {
				newErrors.iban = "Ungültige IBAN";
			}
		}

		// BIC validation
		if (!assistantForm.bic.trim()) {
			newErrors.bic = "BIC ist erforderlich";
		} else {
			const bicCleaned = assistantForm.bic.replace(/\s/g, "");
			if (bicCleaned.length < 8 || bicCleaned.length > 11) {
				newErrors.bic = "BIC muss zwischen 8 und 11 Zeichen lang sein";
			} else if (!isValidBIC(bicCleaned)) {
				newErrors.bic = "Ungültige BIC";
			}
		}

		// Steuer-ID validation
		if (!assistantForm.idnr.trim()) {
			newErrors.idnr = "Steuer-ID ist erforderlich";
		} else {
			const idnrCleaned = assistantForm.idnr.replace(/\s/g, "");
			if (idnrCleaned.length !== 11) {
				newErrors.idnr = "Steuer-ID muss genau 11 Zeichen lang sein";
			} else if (!taxIdValidator.validate(idnrCleaned)) {
				newErrors.idnr = "Ungültige Steuer-ID";
			}
		}

		// SV-Nummer validation (skip if checkbox is checked)
		if (!noAssistantSozialversicherung) {
			if (!assistantForm.sozialversicherung.trim()) {
				newErrors.sozialversicherung = "Bitte geben Sie eine Sozialversicherungsnummer ein oder aktivieren Sie die Option 'Ich habe keine Sozialversicherungsnummer'";
			} else {
				const svnr = assistantForm.sozialversicherung.trim();
				if (svnr.length !== 12) {
					newErrors.sozialversicherung = "SV-Nummer muss genau 12 Zeichen lang sein";
				} else if (!/^.{8}[A-Z]/.test(svnr)) {
					newErrors.sozialversicherung = "Das 9. Zeichen der SV-Nummer muss ein Großbuchstabe sein";
				}
			}
		}

		// Birth fields validation (required when no SV number)
		if (noAssistantSozialversicherung) {
			if (!assistantForm.city_of_birth.trim()) {
				newErrors.city_of_birth = "Geburtsort ist erforderlich";
			}
			if (!assistantForm.country_of_birth) {
				newErrors.country_of_birth = "Geburtsland ist erforderlich";
			}
			if (!assistantForm.geburtsname.trim()) {
				newErrors.geburtsname = "Geburtsname ist erforderlich";
			}
		}

		// Date of birth validation
		if (!assistantForm.date_of_birth.trim()) {
			newErrors.date_of_birth = "Geburtsdatum ist erforderlich";
		} else {
			const dob = new Date(assistantForm.date_of_birth);
			const now = new Date();
			if (dob > now) {
				newErrors.date_of_birth = "Geburtsdatum darf nicht in der Zukunft liegen";
			} else if (dob.getFullYear() < 1900) {
				newErrors.date_of_birth = "Ungültiges Geburtsdatum";
			}
		}

		// Required text fields
		if (!assistantForm.first_name.trim()) newErrors.first_name = "Vorname ist erforderlich";
		if (!assistantForm.last_name.trim()) newErrors.last_name = "Nachname ist erforderlich";
		if (!assistantForm.street.trim()) newErrors.street = "Straße ist erforderlich";
		if (!assistantForm.postcode.trim()) newErrors.postcode = "Postleitzahl ist erforderlich";
		if (!assistantForm.city.trim()) newErrors.city = "Stadt ist erforderlich";
		if (!assistantForm.insurance_type) newErrors.insurance_type = "Versicherungsart ist erforderlich";
		if (!assistantForm.insurance_company.trim()) newErrors.insurance_company = "Versicherung ist erforderlich";

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleToggleAssistant = async () => {
		if (needsAssistant) {
			// Turning off — delete assistant
			if (assistant) {
				try {
					await deleteAssistant(assistant.assistant_id);
				} catch (err) {
					console.error("Error deleting assistant:", err);
				}
			}
			setNeedsAssistant(false);
			const empty = {
				first_name: "",
				last_name: "",
				date_of_birth: "",
				city_of_birth: "",
				country_of_birth: "",
				geburtsname: "",
				nationality: "",
				id_number: "",
				main_occupation_id: "",
				street: "",
				postcode: "",
				city: "",
				iban: "",
				bic: "",
				idnr: "",
				sozialversicherung: "",
				insurance_type: "",
				insurance_company: "",
			};
			setAssistantForm(empty);
			savedDataRef.current = empty;
			setIsDirty(false);
			setErrors({});
			setNoAssistantSozialversicherung(false);
		} else {
			setNeedsAssistant(true);
		}
	};

	const handleSave = async () => {
		if (needsAssistant && !validateAssistantForm()) {
			return;
		}

		setSaving(true);
		try {
			await updateExtra(user?.extraId!, {
				prm_disability_type: disabilityType,
			});

			if (needsAssistant) {
				const payload = {
					...assistantForm,
					nationality: assistantForm.nationality ? Number(assistantForm.nationality) : null,
					main_occupation_id: assistantForm.main_occupation_id ? Number(assistantForm.main_occupation_id) : null,
					sozialversicherung: noAssistantSozialversicherung ? null : (assistantForm.sozialversicherung?.replace(/\s/g, "") || null),
					country_of_birth: assistantForm.country_of_birth ? Number(assistantForm.country_of_birth) : undefined,
					city_of_birth: noAssistantSozialversicherung ? assistantForm.city_of_birth : undefined,
					geburtsname: noAssistantSozialversicherung ? assistantForm.geburtsname : undefined,
					iban: assistantForm.iban.replace(/\s/g, ""),
					bic: assistantForm.bic.replace(/\s/g, ""),
					idnr: assistantForm.idnr.replace(/\s/g, ""),
				};
				if (assistant) {
					await updateAssistant(assistant.assistant_id, payload);
				} else {
					await createAssistant(payload);
				}
				await refetchAssistant();
				savedDataRef.current = { ...assistantForm };
				setIsDirty(false);
			}

			await refetchExtra();
		} catch (err) {
			console.error("Error saving PRM data:", err);
		} finally {
			setSaving(false);
		}
	};

	const loading = extraLoading || assistantLoading;

	if (loading) {
		return (
			<MainLayout
				showBack={true}
				onBack={() => navigateDirectly("app-profile-completion-overview")}
			>
				<div className="flex items-center justify-center min-h-screen">
					<div className="w-12 h-12 border-4 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin"></div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout
			showBack={true}
			onBack={() => navigateDirectly("app-profile-completion-overview")}
		>
			<div className="bg-[var(--ucm-bg-gray)] min-h-screen pb-6">
				<div className="px-4 pt-6 space-y-6">
					{/* Header */}
					<div>
						<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)]">
							Angaben zu Ihrer Begleitperson (optional)
						</h1>
						<p className="text-sm text-[var(--ucm-text-medium)] mt-1">
							Hier können Sie Angaben zu Ihrer Begleitperson machen. Die Angabe einer Begleitperson ist <b>optional</b>.
							Sie können <b>maximal</b> eine Begleitperson für den Probebetrieb mitnehmen.
						</p>
					</div>

					{/* Single Card */}
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						<div className="space-y-4">
							{/* Disability Type */}
							<div>
								<div className="flex items-center space-x-2 mb-2">
									<label
										htmlFor="prm_disability_type"
										className="text-sm font-medium text-[var(--ucm-text-medium)]"
									>
										Art der Beeinträchtigung (freiwillig)
									</label>
									<div className="relative group">
										<svg
											className="w-5 h-5 text-[var(--ucm-info-blue)] cursor-help"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
												clipRule="evenodd"
											/>
										</svg>
										<div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-[var(--ucm-text-dark)] text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
											Es geht hier um die teilnehmende Person mit Einschränkung. Nicht um die Begleitung. Diese Information hilft uns, den Probebetrieb besser zu planen und die Tests entsprechend auszurichten. Die Angabe ist dennoch freiwillig.
											<div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-[var(--ucm-text-dark)]"></div>
										</div>
									</div>
								</div>
								<input
									type="text"
									id="prm_disability_type"
									value={disabilityType}
									onChange={(e) => setDisabilityType(e.target.value)}
									placeholder="z.B. Rollstuhlfahrer, Gehbehinderung, Sehbehinderung"
									className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)]"
								/>
							</div>

							{/* Assistant Toggle */}
							<label className="flex items-center justify-between cursor-pointer">
								<span className="text-sm font-medium text-[var(--ucm-text-dark)]">
									Ich werde am Probebetrieb mit einer Begleitung teilnehmen.
								</span>
								<div className="relative">
									<input
										type="checkbox"
										checked={needsAssistant}
										onChange={handleToggleAssistant}
										className="sr-only peer"
									/>
									<div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[var(--ucm-primary-teal)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--ucm-primary-teal)]"></div>
								</div>
							</label>

							{/* Assistant Form */}
							{needsAssistant && (
								<>
									<p className="text-sm text-[var(--ucm-text-medium)] bg-[var(--ucm-bg-light,#f5f5f5)] rounded-xl px-4 py-3">
										Bitte beachten Sie, dass Sie die untenstehenden Datenfelder für Ihre Begleitung/Assistenz vollständig ausfüllen müssen. Ihre Begleitung/Assistenz muss ebenfalls einen Vertrag unterschreiben, der hier in der Web-App nach Buchungsbestätigung zur Verfügung stehen wird.
									</p>

									<h3 className="text-base font-semibold text-[var(--ucm-text-dark)] pt-2">
										Persönliche Daten des Assistenten
									</h3>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Vorname <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.first_name}
											onChange={(e) => handleAssistantChange("first_name", e.target.value)}
											placeholder="Vorname"
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.first_name ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.first_name && <p className="mt-1 text-sm text-red-500">{errors.first_name}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Nachname <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.last_name}
											onChange={(e) => handleAssistantChange("last_name", e.target.value)}
											placeholder="Nachname"
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.last_name ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.last_name && <p className="mt-1 text-sm text-red-500">{errors.last_name}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Geburtsdatum <span className="text-red-500">*</span>
										</label>
										<input
											type="date"
											value={assistantForm.date_of_birth}
											onChange={(e) => handleAssistantChange("date_of_birth", e.target.value)}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.date_of_birth ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.date_of_birth && <p className="mt-1 text-sm text-red-500">{errors.date_of_birth}</p>}
									</div>

									{/* Nationality */}
									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Staatsangehörigkeit <span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<select
												value={assistantForm.nationality}
												onChange={(e) => handleAssistantChange("nationality", e.target.value)}
												className={`w-full px-4 py-3 pr-12 border rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.nationality ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
											>
												<option value="">Bitte wählen</option>
												{countries.map((country) => (
													<option key={country.country_id} value={country.country_id}>
														{country.nationality}
													</option>
												))}
											</select>
											<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
										</div>
										{errors.nationality && <p className="mt-1 text-sm text-red-500">{errors.nationality}</p>}
									</div>

									{/* ID-Nummer */}
									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											ID-Nummer <span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<input
												type="text"
												value={assistantForm.id_number}
												onChange={(e) => handleAssistantChange("id_number", e.target.value)}
												placeholder="Personalausweis- oder Reisepassnummer"
												maxLength={50}
												className={`w-full px-4 pr-10 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.id_number ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
											/>
											<Info
												className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ucm-text-light)] cursor-pointer hover:text-[var(--ucm-primary-teal)] transition-colors"
												onClick={() => setShowAssistantIdInfoModal(true)}
											/>
										</div>
										{errors.id_number && <p className="mt-1 text-sm text-red-500">{errors.id_number}</p>}
									</div>

									{/* Occupation */}
									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Hauptbeschäftigung <span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<select
												value={assistantForm.main_occupation_id}
												onChange={(e) => handleAssistantChange("main_occupation_id", e.target.value)}
												className={`w-full px-4 py-3 pr-12 border rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.main_occupation_id ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
											>
												<option value="">Bitte wählen</option>
												{occupations.map((occupation) => (
													<option key={occupation.id} value={occupation.id}>
														{occupation.name}
													</option>
												))}
											</select>
											<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
										</div>
										{errors.main_occupation_id && <p className="mt-1 text-sm text-red-500">{errors.main_occupation_id}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Straße und Hausnummer <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.street}
											onChange={(e) => handleAssistantChange("street", e.target.value)}
											placeholder="z.B. Musterstraße 123"
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.street ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.street && <p className="mt-1 text-sm text-red-500">{errors.street}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Postleitzahl <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.postcode}
											onChange={(e) => handleAssistantChange("postcode", e.target.value)}
											placeholder="z.B. 60547"
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.postcode ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.postcode && <p className="mt-1 text-sm text-red-500">{errors.postcode}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Stadt <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.city}
											onChange={(e) => handleAssistantChange("city", e.target.value)}
											placeholder="z.B. Frankfurt"
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.city ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.city && <p className="mt-1 text-sm text-red-500">{errors.city}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											IBAN <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.iban}
											onChange={(e) => handleAssistantChange("iban", e.target.value)}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.iban ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.iban && <p className="mt-1 text-sm text-red-500">{errors.iban}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											BIC <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.bic}
											onChange={(e) => handleAssistantChange("bic", e.target.value)}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.bic ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.bic && <p className="mt-1 text-sm text-red-500">{errors.bic}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Steuer-ID <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.idnr}
											onChange={(e) => handleAssistantChange("idnr", e.target.value)}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.idnr ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.idnr && <p className="mt-1 text-sm text-red-500">{errors.idnr}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Sozialversicherungsnummer
										</label>
										<input
											type="text"
											value={assistantForm.sozialversicherung}
											onChange={(e) => handleAssistantChange("sozialversicherung", e.target.value)}
											disabled={noAssistantSozialversicherung}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.sozialversicherung ? "border-red-500" : "border-[var(--ucm-border-light)]"} ${noAssistantSozialversicherung ? "bg-gray-100 cursor-not-allowed" : ""}`}
										/>
										{errors.sozialversicherung && <p className="mt-1 text-sm text-red-500">{errors.sozialversicherung}</p>}
									</div>

									<div className="flex items-center space-x-3">
										<input
											type="checkbox"
											id="no_assistant_sozialversicherung"
											checked={noAssistantSozialversicherung}
											onChange={(e) => handleAssistantSozialCheckbox(e.target.checked)}
											className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)]"
										/>
										<label
											htmlFor="no_assistant_sozialversicherung"
											className="text-base font-medium text-[var(--ucm-text-dark)]"
										>
											Ich habe keine Sozialversicherungsnummer
										</label>
									</div>
									{!assistantForm.sozialversicherung && !noAssistantSozialversicherung && (
										<p className="text-sm text-[var(--ucm-info-blue)]">
											Bitte geben Sie entweder eine Sozialversicherungsnummer ein oder aktivieren Sie diese Option
										</p>
									)}

									{/* Conditional Birth Fields (when no SV number) */}
									{noAssistantSozialversicherung && (
										<div className="space-y-4 pt-4 border-t border-[var(--ucm-border-light)]">
											{/* City of Birth - Geburtsort */}
											<div>
												<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
													Geburtsort <span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													value={assistantForm.city_of_birth}
													onChange={(e) => handleAssistantChange("city_of_birth", e.target.value)}
													placeholder="Bitte geben Sie den Geburtsort ein"
													className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.city_of_birth ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
												/>
												{errors.city_of_birth && <p className="mt-1 text-sm text-red-500">{errors.city_of_birth}</p>}
											</div>

											{/* Country of Birth - Geburtsland */}
											<div>
												<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
													Geburtsland <span className="text-red-500">*</span>
												</label>
												<div className="relative">
													<select
														value={assistantForm.country_of_birth}
														onChange={(e) => handleAssistantChange("country_of_birth", e.target.value)}
														className={`w-full px-4 py-3 pr-12 border rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.country_of_birth ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
													>
														<option value="">Land auswählen</option>
														{countries.map((country) => (
															<option key={country.country_id} value={country.country_id}>
																{country.country_name}
															</option>
														))}
													</select>
													<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
												</div>
												{errors.country_of_birth && <p className="mt-1 text-sm text-red-500">{errors.country_of_birth}</p>}
											</div>

											{/* Geburtsname */}
											<div>
												<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
													Geburtsname <span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													value={assistantForm.geburtsname}
													onChange={(e) => handleAssistantChange("geburtsname", e.target.value)}
													placeholder="Bitte geben Sie den Geburtsnamen ein"
													className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.geburtsname ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
												/>
												{errors.geburtsname && <p className="mt-1 text-sm text-red-500">{errors.geburtsname}</p>}
											</div>
										</div>
									)}

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Versicherungsart <span className="text-red-500">*</span>
										</label>
										<select
											value={assistantForm.insurance_type}
											onChange={(e) => handleAssistantChange("insurance_type", e.target.value)}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] appearance-none pr-10 ${errors.insurance_type ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										>
											<option value="">Bitte wählen</option>
											<option value="gesetzlich">Gesetzlich</option>
											<option value="privat">Privat</option>
										</select>
										{errors.insurance_type && <p className="mt-1 text-sm text-red-500">{errors.insurance_type}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Versicherung <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={assistantForm.insurance_company}
											onChange={(e) => handleAssistantChange("insurance_company", e.target.value)}
											placeholder="z.B. AOK, TK, Barmer"
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${errors.insurance_company ? "border-red-500" : "border-[var(--ucm-border-light)]"}`}
										/>
										{errors.insurance_company && <p className="mt-1 text-sm text-red-500">{errors.insurance_company}</p>}
									</div>
								</>
							)}

							{/* Save Button */}
							<button
								onClick={handleSave}
								disabled={saving}
								className="w-full py-3 bg-[var(--ucm-primary-teal)] text-white rounded-xl font-semibold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{saving ? (
									<span className="flex items-center justify-center">
										<Loader2 className="w-5 h-5 animate-spin mr-2" />
										Speichern...
									</span>
								) : (
									"Speichern"
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
			{/* Assistant ID Number Info Modal */}
			{showAssistantIdInfoModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Hinweis zur ID-Nummer
						</h2>
						<div className="space-y-4 text-[var(--ucm-text-medium)]">
							<p>
								Sie können hier jede gültige Ausweisnummer eingeben
								(Personalausweis oder Reisepass). Der deutsche Personalausweis
								wird jedoch bevorzugt.
							</p>
							<p className="font-medium">
								Wichtig: Bitte bringen Sie zum Probebetrieb genau das Dokument
								mit, dessen Nummer Sie hier eingeben. Dies wird zur
								Verifizierung benötigt.
							</p>
						</div>
						<div className="mt-6">
							<button
								onClick={() => setShowAssistantIdInfoModal(false)}
								className="w-full px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Verstanden
							</button>
						</div>
					</div>
				</div>
			)}
		</MainLayout>
	);
}
